import { isDataSourceCredentialsPending } from '../../data-source-credentials-pending';

const LEGACY_CLOUDINARY_PROTOCOL = 'cloudinary:';
const CREDENTIAL_MASK = '**************';

export interface CloudinaryCredentialConfig {
	environment?: string | null;
	accessToken?: string | null;
	apiSecret?: string | null;
}

export interface CloudinaryRequestCredentials {
	cloud_name: string;
	api_key: string;
	api_secret: string;
}

export type CloudinaryCredentialField = 'cloud name' | 'API key' | 'API secret';

/** Contains only configuration reasons and field labels, never credential values. */
export class CloudinaryCredentialsError extends Error {
	constructor(
		readonly kind: 'custom' | 'workspace',
		readonly missingFields: readonly CloudinaryCredentialField[] = []
	) {
		super(
			kind === 'custom'
				? 'Cloudinary custom authentication requires all of cloud name, API key, and API secret.'
				: 'Cloudinary workspace credentials are not configured.'
		);
		this.name = 'CloudinaryCredentialsError';
	}
}

export function missingCloudinaryCredentialFields(config: CloudinaryCredentialConfig): CloudinaryCredentialField[] {
	const fields: [keyof CloudinaryCredentialConfig, CloudinaryCredentialField][] = [
		['environment', 'cloud name'],
		['accessToken', 'API key'],
		['apiSecret', 'API secret']
	];
	return fields.filter(([key]) => !usable(config[key])).map(([, label]) => label);
}

function usable(value: string | null | undefined): value is string {
	return typeof value === 'string' && value.trim().length > 0 && value !== CREDENTIAL_MASK;
}

function parseLegacyCloudinaryUrl(value: string | null | undefined): CloudinaryRequestCredentials | undefined {
	if (typeof value !== 'string') {
		return undefined;
	}
	const normalizedValue = value.trim();
	if (!normalizedValue.toLowerCase().startsWith(`${LEGACY_CLOUDINARY_PROTOCOL}//`)) return undefined;

	try {
		const url = new URL(normalizedValue);
		const apiKey = decodeURIComponent(url.username);
		const apiSecret = decodeURIComponent(url.password);
		if (url.protocol !== LEGACY_CLOUDINARY_PROTOCOL || !usable(url.hostname) || !usable(apiKey) || !usable(apiSecret)) {
			return undefined;
		}
		return { cloud_name: url.hostname, api_key: apiKey, api_secret: apiSecret };
	} catch {
		return undefined;
	}
}

export function normalizeCloudinaryConnectorConfig<T extends CloudinaryCredentialConfig>(config: T): T {
	const normalized = { ...config };
	const environment = typeof normalized.environment === 'string' ? normalized.environment.trim() : undefined;
	if (!environment?.toLowerCase().startsWith('cloudinary:')) {
		return normalized;
	}

	const legacy = parseLegacyCloudinaryUrl(environment);
	if (!legacy) {
		delete normalized.environment;
		return normalized;
	}

	normalized.environment = legacy.cloud_name;
	normalized.accessToken = legacy.api_key;
	normalized.apiSecret = legacy.api_secret;
	return normalized;
}

/**
 * Whether a config asks for per-source credentials instead of the workspace default.
 *
 * Only usable values count. Data sources saved before the connection wizard
 * persisted `environment: ''` for a blank field, and testing `!== undefined`
 * read those as a half-filled custom setup, so they failed the completeness
 * check instead of falling back to the workspace URL.
 *
 * A lone `accessToken` is deliberately not a signal: that field predates the
 * wizard and was accepted but never used when querying, so on its own it is
 * legacy residue. `apiSecret` exists only on configs the wizard wrote. Every
 * other combination is an intentional custom setup, and an incomplete one is
 * still reported through CloudinaryCredentialsError.
 */
export function hasAnyCloudinaryCredentialField(config: CloudinaryCredentialConfig): boolean {
	return usable(config.environment) || usable(config.apiSecret);
}

export function hasCompleteCloudinaryCredentials(config: CloudinaryCredentialConfig): boolean {
	if (!hasAnyCloudinaryCredentialField(config)) {
		return true;
	}
	return missingCloudinaryCredentialFields(config).length === 0;
}

/** Whether the config carries either half of the secret pair. */
function hasPartialCloudinaryCredentials(config: CloudinaryCredentialConfig): boolean {
	return usable(config.accessToken) || usable(config.apiSecret);
}

/**
 * Whether the credential pair must be present and complete.
 *
 * A credential-pending config — one that arrived through bulk import, whose
 * export strips every secret — may carry a cloud name on its own: the structure
 * lands and an administrator supplies the secrets afterwards. Half of the pair
 * is still an incomplete custom setup and is still rejected, on every path.
 */
export function requiresCloudinaryCredentials(config: CloudinaryCredentialConfig): boolean {
	if (!hasAnyCloudinaryCredentialField(config)) {
		return false;
	}

	return !isDataSourceCredentialsPending(config) || hasPartialCloudinaryCredentials(config);
}

export function resolveCloudinaryRequestCredentials(
	config: CloudinaryCredentialConfig,
	workspaceUrl: string | undefined
): CloudinaryRequestCredentials {
	const normalized = normalizeCloudinaryConnectorConfig(config);
	if (hasAnyCloudinaryCredentialField(normalized)) {
		const missingFields = missingCloudinaryCredentialFields(normalized);
		if (missingFields.length) {
			throw new CloudinaryCredentialsError('custom', missingFields);
		}
		return {
			cloud_name: normalized.environment.trim(),
			api_key: normalized.accessToken,
			api_secret: normalized.apiSecret
		};
	}

	const workspace = parseLegacyCloudinaryUrl(workspaceUrl);
	if (!workspace) {
		throw new CloudinaryCredentialsError('workspace');
	}
	return workspace;
}
